export const GOOGLE_AD_SUITABILITY_CONTRACT = 'watchvim-google-ad-suitability-v1';
export const GOOGLE_AD_SUITABILITY_VERSION = 1;
export const GOOGLE_AD_CLASSIFICATIONS = Object.freeze(['unreviewed', 'standard', 'restricted', 'blocked']);

const INDEX_KEY = '__WATCHVIM_GOOGLE_AD_SUITABILITY_BY_TITLE__';

function first(...values) {
  return values.find((value) => value !== undefined && value !== null && String(value).trim() !== '');
}

function bool(value, fallback = true) {
  if (value === true || value === false) return value;
  const normalized = String(value == null ? '' : value).trim().toLowerCase();
  if (['true', 'yes', '1', 'enabled', 'allow', 'allowed'].includes(normalized)) return true;
  if (['false', 'no', '0', 'disabled', 'deny', 'denied'].includes(normalized)) return false;
  return fallback;
}

function list(value) {
  if (Array.isArray(value)) return value.map((entry) => String(entry || '').trim()).filter(Boolean);
  if (!value) return [];
  return String(value).split(/[,|;]/).map((entry) => entry.trim()).filter(Boolean);
}

export function normalizeGoogleAdSuitability(raw = {}, inherited = null) {
  const nested = raw?.googleAds && typeof raw.googleAds === 'object' ? raw.googleAds : {};
  const inheritedPolicy = inherited ? normalizeGoogleAdSuitability(inherited) : null;
  const requested = String(first(
    raw.googleAdsClassification,
    raw.google_ads_classification,
    nested.classification,
    inheritedPolicy?.classification,
    'unreviewed',
  )).trim().toLowerCase();
  // Publisher-approved AVOD policy; explicit Restricted/Blocked decisions still win.
  const avodApproval = raw?.monetization?.avod === true && requested === 'unreviewed';
  const classification = avodApproval ? 'standard' : (GOOGLE_AD_CLASSIFICATIONS.includes(requested) ? requested : 'unreviewed');
  const reasons = list(first(
    raw.googleAdsRestrictionReasons,
    raw.google_ads_restriction_reasons,
    raw.googleAdsRestrictionReason,
    nested.reasons,
    inheritedPolicy?.reasons,
    [],
  ));
  const allowDirectAds = bool(first(
    raw.allowWatchVimDirectAds,
    raw.allowWatchVIMDirectAds,
    nested.allowDirectAds,
    inheritedPolicy?.allowDirectAds,
  ), true);
  const reviewedAt = String(first(raw.googleAdsReviewedAt, nested.reviewedAt, inheritedPolicy?.reviewedAt, avodApproval ? '2026-09-30T16:59:36Z' : '') || '').trim();
  const reviewedBy = String(first(raw.googleAdsReviewedBy, nested.reviewedBy, inheritedPolicy?.reviewedBy, avodApproval ? 'Anthony Bawn' : '') || '').trim();
  const reviewNote = String(first(raw.googleAdsReviewNote, nested.reviewNote, inheritedPolicy?.reviewNote, avodApproval ? 'Approved under Anthony Bawn’s AVOD Google advertising policy authorized on 2026-09-30; selecting AVOD enables Google demand.' : '') || '').trim();

  return Object.freeze({
    contract: GOOGLE_AD_SUITABILITY_CONTRACT,
    version: GOOGLE_AD_SUITABILITY_VERSION,
    classification,
    reasons,
    allowDirectAds,
    reviewedAt,
    reviewedBy,
    reviewNote,
    googleDemandEligible: classification === 'standard',
    googleDemandSuppressed: classification !== 'standard',
  });
}

function titlePolicy(item) {
  return normalizeGoogleAdSuitability(item?.raw || item || {});
}

export function indexGoogleAdSuitability(catalog = {}) {
  const map = new Map();
  for (const item of catalog?.titles || []) {
    if (!item?.id) continue;
    const policy = titlePolicy(item);
    map.set(String(item.id), policy);
    if (item.slug) map.set(String(item.slug), policy);
  }
  if (typeof window !== 'undefined') window[INDEX_KEY] = map;
  return map;
}

export function googleAdSuitabilityForTitleId(titleId) {
  if (typeof window === 'undefined') return normalizeGoogleAdSuitability({});
  const map = window[INDEX_KEY];
  if (!(map instanceof Map)) return normalizeGoogleAdSuitability({});
  return map.get(String(titleId || '')) || normalizeGoogleAdSuitability({});
}

export function titleIdFromWatchPath(pathname = '') {
  const path = String(pathname || '').split('?')[0].split('#')[0];
  let match = path.match(/^\/watch\/([^/]+)\/?$/);
  if (match) return decodeURIComponentSafe(match[1]);
  match = path.match(/^\/watch\/([^/]+)\/season\/[^/]+\/episode\/[^/]+\/?$/);
  return match ? decodeURIComponentSafe(match[1]) : '';
}

function decodeURIComponentSafe(value) {
  try { return decodeURIComponent(value); } catch (_) { return String(value || ''); }
}

export function isGoogleDemandUrl(value = '') {
  const raw = String(value || '').trim();
  if (!raw) return false;
  try {
    const host = new URL(raw, 'https://watchvim.com').hostname.toLowerCase();
    return host === 'pubads.g.doubleclick.net'
      || host.endsWith('.doubleclick.net')
      || host === 'pagead2.googlesyndication.com'
      || host.endsWith('.googlesyndication.com')
      || host.endsWith('.googleadservices.com');
  } catch (_) {
    return /(?:doubleclick\.net|googlesyndication\.com|googleadservices\.com)/i.test(raw);
  }
}
